// web/frontend/src/pages/settings/IntegrationsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Link2, Zap, Lock, ArrowRight, CheckCircle2, X,
  ExternalLink, ChevronRight, ChevronDown, ChevronUp,
  RefreshCw, Bell, Unlock, Plug
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { SkeletonBlock, SkeletonCard } from '../../components/Skeleton';
import { useAuth } from '../../context/AuthContext';

// ─── Integration definitions with categories ───
const ALL_INTEGRATIONS = [
  // CRM
  {
    id: 'hubspot', name: 'HubSpot CRM', category: 'CRM',
    icon: 'https://www.hubspot.com/favicon.ico',
    description: 'Auto-create contacts with AI scores and pitch angles',
    color: '#FF7A59', comingSoon: false,
  },
  {
    id: 'salesforce', name: 'Salesforce', category: 'CRM',
    icon: 'https://upload.wikimedia.org/wikipedia/commons/f/f9/Salesforce.com_logo.svg',
    description: 'Enterprise CRM,  leads flow directly into sales queues',
    color: '#00A1E0', comingSoon: true,
  },
  {
    id: 'pipedrive', name: 'Pipedrive', category: 'CRM',
    icon: 'https://www.pipedrive.com/favicon.ico',
    description: 'Deal-focused CRM,  each lead becomes a deal',
    color: '#1DB954', comingSoon: false,
  },
  // Export & Data
  {
    id: 'google_sheets', name: 'Google Sheets', category: 'Export & Data',
    icon: 'https://upload.wikimedia.org/wikipedia/commons/3/30/Google_Sheets_logo_%282014-2020%29.svg',
    description: 'Export leads directly to Google Sheets in one click',
    color: '#34a853', comingSoon: false,
  },
  {
    id: 'airtable', name: 'Airtable', category: 'Export & Data',
    icon: 'https://airtable.com/favicon.ico',
    description: 'Rich lead database with custom views and filters',
    color: '#18BFFF', comingSoon: false,
  },
  // Automation
  {
    id: 'zapier', name: 'Zapier', category: 'Automation',
    icon: 'https://zapier.com/favicon.ico',
    description: 'Connect to 5,000+ apps like Gmail, Slack, HubSpot & more',
    color: '#FF4A00', comingSoon: true,
  },
  // Communication
  {
    id: 'slack', name: 'Slack', category: 'Communication',
    icon: 'https://upload.wikimedia.org/wikipedia/commons/d/d5/Slack_icon_2019.svg',
    description: 'Get hot lead alerts instantly in your sales channel',
    color: '#4A154B', comingSoon: false,
  },
  // Marketing
  {
    id: 'mailchimp', name: 'Mailchimp', category: 'Marketing',
    icon: 'https://mailchimp.com/favicon.ico',
    description: 'Add leads to email campaigns with smart tags',
    color: '#FFE01B', comingSoon: false,
  },
  // Productivity
  {
    id: 'notion', name: 'Notion', category: 'Productivity',
    icon: 'https://upload.wikimedia.org/wikipedia/commons/e/e9/Notion-logo.svg',
    description: 'Sync leads to your Notion workspace automatically',
    color: '#000000', comingSoon: false,
  },
  // Developer
  {
    id: 'webhook', name: 'Webhooks', category: 'Developer',
    icon: 'https://cdn-icons-png.flaticon.com/512/2920/2920277.png',
    description: 'Send lead data to any custom endpoint or API',
    color: '#6366f1', comingSoon: false,
  },
];

const PLAN_GATE = {
  google_sheets: ['growth', 'pro', 'enterprise'],
  hubspot: ['pro', 'enterprise'],
  salesforce: ['enterprise'],
  slack: ['growth', 'pro', 'enterprise'],
  zapier: ['growth', 'pro', 'enterprise'],
  webhook: ['pro', 'enterprise'],
  mailchimp: ['growth', 'pro', 'enterprise'],
  airtable: ['growth', 'pro', 'enterprise'],
  notion: ['growth', 'pro', 'enterprise'],
  pipedrive: ['growth', 'pro', 'enterprise'],
};

;
function DisconnectModal({ integration, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999,
    }} onClick={onCancel}>
      <div style={{
        background: 'var(--bg-secondary)', borderRadius: 20, padding: '28px 32px',
        maxWidth: 420, width: '90%', border: '1px solid var(--border-primary)',
        boxShadow: 'var(--shadow-xl)', textAlign: 'center',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
        <h3 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>
          Disconnect {integration?.name}?
        </h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 20px', lineHeight: 1.6 }}>
          You'll need to re-authorize to reconnect. Your existing exports won't be affected.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onCancel} style={{
            flex: 1, padding: '12px', borderRadius: 10,
            border: '1px solid var(--border-primary)', background: 'transparent',
            color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 600,
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            flex: 1, padding: '12px', borderRadius: 10, border: 'none',
            background: 'var(--error)', color: 'white', cursor: 'pointer',
            fontSize: 14, fontWeight: 700,
          }}>Disconnect</button>
        </div>
      </div>
    </div>
  );
}

export default function IntegrationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const currentPlan = user?.plan || 'free';
  const isFree = currentPlan === 'free';

  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(null);
  const [expandedConfig, setExpandedConfig] = useState(null); // id of integration with open config
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState(['lead_created', 'lead_scored', 'search_completed']);
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(null);

  const isLocked = (id) => {
    const allowed = PLAN_GATE[id];
    if (!allowed) return false;
    return !allowed.includes(currentPlan);
  };

  const requiredPlan = (id) => {
    const allowed = PLAN_GATE[id];
    if (!allowed) return 'Free';
    return allowed[0].charAt(0).toUpperCase() + allowed[0].slice(1);
  };

  const loadIntegrations = async () => {
    try {
      const res = await api.get('/api/integrations');
      const apiList = res.data.integrations || [];
      const merged = ALL_INTEGRATIONS.map(item => {
        const match = apiList.find(a => a.id === item.id);
        return match ? { ...item, connected: match.connected, connectedAt: match.connectedAt } : item;
      });
      setIntegrations(merged);
    } catch {
      setIntegrations(ALL_INTEGRATIONS.map(i => ({ ...i, connected: false })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadIntegrations(); }, []);

  const handleConnect = async (integration) => {
    if (['google_sheets', 'hubspot', 'salesforce', 'slack', 'zapier', 'notion', 'mailchimp', 'pipedrive', 'airtable'].includes(integration.id)) {
      setLoadingAction(integration.id);
      try {
        const res = await api.post(`/api/integrations/${integration.id}/connect`);
        if (!res.data.auth_url) { toast.error('Connection URL not available'); setLoadingAction(null); return; }
        const popup = window.open(res.data.auth_url, `${integration.id}-connect`, 'width=600,height=700');
        if (!popup) { toast.info('Please allow popups'); setLoadingAction(null); return; }
        const handler = (event) => {
          if (event.data === `${integration.id}_connected` || event.data === 'integration_connected') {
            window.removeEventListener('message', handler);
            clearInterval(interval);
            setIntegrations(prev => prev.map(i => i.id === integration.id ? { ...i, connected: true, connectedAt: new Date().toISOString() } : i));
            toast.success(`${integration.name} connected!`);
            setLoadingAction(null);
          }
        };
        window.addEventListener('message', handler);
        const interval = setInterval(() => {
          if (popup.closed) { clearInterval(interval); window.removeEventListener('message', handler); loadIntegrations(); setLoadingAction(null); }
        }, 1000);
      } catch (err) {
        const msg = err.response?.data?.detail || 'Connection failed';
        if (integration.id === 'google_sheets') {
          toast.error('Google Sheets API not configured yet. Set up Google Cloud Console credentials with Sheets API scope.', { duration: 8000 });
        } else {
          toast.error(msg);
        }
        setLoadingAction(null);
      }
      return;
    }
    // For webhook-based: toggle inline config
    if (['webhook'].includes(integration.id)) {
      setExpandedConfig(expandedConfig === integration.id ? null : integration.id);
      setWebhookUrl('');
      setWebhookEvents(['lead_created', 'lead_scored', 'search_completed']);
      return;
    }
    toast.info(`${integration.name} coming soon!`);
  };

  const handleSaveConfig = async (integration) => {
    if (!webhookUrl) {
      toast.error('Please enter a URL or API key');
      return;
    }
    setWebhookSaving(true);
    try {
      const payload = false
        ? { url: webhookUrl, events: ['lead_created'] }
        : { url: webhookUrl, events: webhookEvents };
      await api.put(`/api/integrations/${integration.id}/configure`, payload);
      toast.success(`${integration.name} configured!`);
      setExpandedConfig(null);
      loadIntegrations();
    } catch { toast.error('Failed to save configuration'); }
    finally { setWebhookSaving(false); }
  };

  const handleDisconnect = async (integration) => {
    if (!confirmDisconnect) {
      setConfirmDisconnect(integration);
      return;
    }
    setDisconnectingId(integration.id);
    try {
      await api.delete(`/api/integrations/${integration.id}`);
      setIntegrations(prev => prev.map(i => i.id === integration.id ? { ...i, connected: false, connectedAt: null } : i));
      toast.success(`${integration.name} disconnected`);
    } catch { toast.error('Failed to disconnect'); }
    finally { setDisconnectingId(null); setConfirmDisconnect(null); }
  };

  // Group by category
  const categories = {};
  integrations.forEach(item => {
    const cat = item.category || 'Other';
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(item);
  });

  const connectedCount = integrations.filter(i => i.connected).length;
  const lockedCount = integrations.filter(i => isLocked(i.id) && !i.connected && !i.comingSoon).length;
  const comingSoonCount = integrations.filter(i => i.comingSoon).length;

  if (loading) {
    return (
      <div className="page-content" style={{ maxWidth: 'clamp(320px, 100%, 1000px)', margin: '0 auto' }}>
        <SkeletonBlock width="220px" height={28} style={{ marginBottom: 8 }} />
        <SkeletonBlock width="400px" height={16} style={{ marginBottom: 24 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-md)' }}>
          {[1,2,3,4,5,6].map(i => <SkeletonCard key={i} lines={2} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="page-content" style={{ maxWidth: 'clamp(320px, 100%, 1000px)', margin: '0 auto' }}>
      
      {/* ================================================================ */}
      {/* HEADER + KPI SUMMARY */}
      {/* ================================================================ */}
      <div style={{ marginBottom: 'var(--space-2xl)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 'var(--space-xl)' }}>
          <div>
            <h1 style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 28 }}>🔌</span> Integrations
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 0 }}>
              Connect iLeadX to your stack. Each integration automates a piece of your outreach workflow.
            </p>
          </div>
        </div>

        {/* KPI Pills */}
        <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
          <div style={kpiPillStyle('var(--success)', 'var(--success-bg)')}>
            <CheckCircle2 size={16} />
            <span style={{ fontWeight: 700 }}>{connectedCount}</span> Active
          </div>
          <div style={kpiPillStyle('var(--warning)', 'var(--warning-bg)')}>
            <Lock size={14} />
            <span style={{ fontWeight: 700 }}>{lockedCount}</span> Locked
          </div>
          <div style={kpiPillStyle('var(--info)', 'var(--info-bg)')}>
            <Bell size={14} />
            <span style={{ fontWeight: 700 }}>{comingSoonCount}</span> Coming Soon
          </div>
        </div>
      </div>

      {/* Free user subtle banner */}
      {isFree && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 14px', borderRadius: 'var(--radius-md)',
          background: 'var(--warning-bg)', border: '1px solid var(--warning)30',
          marginBottom: 'var(--space-lg)', color: 'var(--warning)', fontSize: 11,
        }}>
          <Unlock size={12} />
          <span style={{ flex: 1 }}>Upgrade to connect integrations. Your data stays synced automatically.</span>
          <button onClick={() => navigate('/dashboard/billing?tab=plans')} style={{
            padding: '3px 10px', borderRadius: 6,
            background: 'var(--accent-amber)', color: '#0F172A', border: 'none',
            cursor: 'pointer', fontWeight: 600, fontSize: 10, whiteSpace: 'nowrap',
          }}>View Plans</button>
        </div>
      )}

      {/* ================================================================ */}
      {/* CATEGORIZED INTEGRATIONS */}
      {/* ================================================================ */}
      {Object.entries(categories).map(([category, items]) => (
        <div key={category} style={{ marginBottom: 'var(--space-2xl)' }}>
          <h3 style={{
            color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 var(--space-md)',
            paddingLeft: 'var(--space-sm)',
          }}>
            {category}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            {items.map(integration => {
              const locked = isLocked(integration.id);
              const isConnected = integration.connected;
              const isComingSoon = integration.comingSoon;

              return (
                <div key={integration.id}>
                  <div style={{
                    background: 'var(--bg-secondary)',
                    border: isConnected ? '1px solid var(--success)' : '1px solid var(--border-primary)',
                    borderLeft: isConnected ? '3px solid var(--success)' : '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-xl)',
                    padding: 'var(--space-lg) var(--space-xl)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    flexWrap: 'wrap', gap: 'var(--space-md)',
                    opacity: isComingSoon ? 0.65 : 1,
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => {
                    if (!isConnected && !isComingSoon) e.currentTarget.style.borderColor = 'var(--accent-primary)';
                  }}
                  onMouseLeave={e => {
                    if (!isConnected && !isComingSoon) e.currentTarget.style.borderColor = 'var(--border-primary)';
                  }}>
                    {/* Icon + Info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-lg)', flex: 1, minWidth: 240 }}>
                      <div style={{
                        width: 52, height: 52, borderRadius: 'var(--radius-lg)',
                        background: `${integration.color}15`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                        position: 'relative',
                      }}>
                        <img src={integration.icon} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                        {isConnected && (
                          <div style={{
                            position: 'absolute', bottom: -2, right: -2,
                            width: 18, height: 18, borderRadius: '50%',
                            background: 'var(--success)', border: '2px solid var(--bg-secondary)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <CheckCircle2 size={12} color="white" />
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 'var(--text-base)' }}>
                            {integration.name}
                          </span>
                          {isConnected && <span style={statusBadge('var(--success)', 'var(--success-bg)')}>Connected</span>}
                          {isComingSoon && <span style={statusBadge('var(--info)', 'var(--info-bg)')}>Coming soon</span>}
                          {locked && !isConnected && !isComingSoon && (
                            <span style={statusBadge('var(--warning)', 'var(--warning-bg)')}>
                              <Lock size={10} /> {requiredPlan(integration.id)}
                            </span>
                          )}
                        </div>
                        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', margin: '4px 0 0', lineHeight: 1.4 }}>
                          {isConnected ? (
    integration.id === 'google_sheets'
      ? (currentPlan === 'pro' || currentPlan === 'enterprise' ? 'Syncing leads automatically' : 'Manual sync via export button')
      : 'Syncing leads automatically'
  ) : integration.description}
                        </p>
                      </div>
                    </div>

                    {/* Action */}
                    <div style={{ flexShrink: 0 }}>
                      {isConnected ? (
                        <button onClick={() => handleDisconnect(integration)} disabled={disconnectingId === integration.id}
                          style={ghostBtnStyle}>
                          {disconnectingId === integration.id ? 'Disconnecting...' : 'Disconnect'}
                        </button>
                      ) : isComingSoon ? (
                        <button style={notifyBtnStyle}>
                          <Bell size={14} /> Notify Me
                        </button>
                      ) : locked ? (
                        <button onClick={() => navigate('/dashboard/billing?tab=plans')}
                          className="auth-button auth-button-primary"
                          style={{ padding: '8px 18px', fontSize: 'var(--text-xs)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Unlock size={14} /> Unlock
                        </button>
                      ) : (
                        <button onClick={() => handleConnect(integration)} disabled={loadingAction === integration.id}
                          className="auth-button auth-button-primary"
                          style={{ padding: '8px 18px', fontSize: 'var(--text-xs)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {loadingAction === integration.id ? 'Connecting...' : <><Plug size={14} /> Connect</>}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Inline config panel (webhook/key inputs) */}
                  {expandedConfig === integration.id && (
                    <div style={{
                      background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)',
                      borderTop: 'none', borderRadius: '0 0 var(--radius-xl) var(--radius-xl)',
                      padding: 'var(--space-xl)', marginTop: -1,
                      animation: 'fadeSlideIn 0.3s ease',
                    }}>
                      <div style={{ marginBottom: 'var(--space-lg)' }}>
                        <label style={labelStyle}>
                          {integration.id === 'mailchimp' ? 'Mailchimp API Key' : 'Webhook URL'}
                        </label>
                        <input
                          type="text"
                          value={webhookUrl}
                          onChange={e => setWebhookUrl(e.target.value)}
                          placeholder={integration.id === 'mailchimp' ? 'key-us1' : 'https://your-app.com/webhook'}
                          style={inputStyle}
                        />
                        {integration.id === 'mailchimp' && (
                          <p style={{ color: 'var(--text-tertiary)', fontSize: 11, marginTop: 4 }}>
                            Find under <strong>Account → Extras → API keys</strong>.
                          </p>
                        )}
                        
                      </div>

                      {/* Events (only for generic webhooks) */}
                      {integration.id !== 'mailchimp' && (
                        <div style={{ marginBottom: 'var(--space-lg)' }}>
                          <label style={labelStyle}>Events to Send</label>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {['lead_created', 'lead_scored', 'search_completed'].map(event => (
                              <label key={event} style={checkboxLabel}>
                                <input
                                  type="checkbox"
                                  checked={webhookEvents.includes(event)}
                                  onChange={e => {
                                    setWebhookEvents(prev => e.target.checked ? [...prev, event] : prev.filter(ev => ev !== event));
                                  }}
                                  style={{ accentColor: 'var(--accent-primary)' }}
                                />
                                {event.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 12 }}>
                        <button onClick={() => setExpandedConfig(null)} style={ghostBtnStyle}>Cancel</button>
                        <button onClick={() => handleSaveConfig(integration)} disabled={webhookSaving}
                          className="auth-button auth-button-primary"
                          style={{ padding: '8px 20px', fontSize: 'var(--text-sm)' }}>
                          {webhookSaving ? 'Saving...' : 'Save Configuration'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {confirmDisconnect && (
        <DisconnectModal
          integration={confirmDisconnect}
          onConfirm={() => handleDisconnect(confirmDisconnect)}
          onCancel={() => setConfirmDisconnect(null)}
        />
      )}

      {/* Request Integration */}
      <div style={{
        marginTop: 'var(--space-xl)', padding: 'var(--space-2xl)',
        background: 'linear-gradient(135deg, var(--accent-glow), rgba(163,113,247,0.08))',
        borderRadius: 'var(--radius-xl)', textAlign: 'center',
        border: '1px solid var(--border-accent)',
      }}>
        <Zap size={24} color="var(--accent-secondary)" style={{ marginBottom: 'var(--space-sm)' }} />
        <p style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 'var(--text-sm)', margin: '0 0 4px' }}>
          Don't see your tool?
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', margin: '0 0 var(--space-md)' }}>
          Let us know which integrations you'd like to see next.
        </p>
        <a href="mailto:support@ileadx.io?subject=Integration Request" style={{
          color: 'var(--accent-primary)', fontSize: 'var(--text-xs)', textDecoration: 'none',
          display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600,
        }}>
          Request Integration <ArrowRight size={14} />
        </a>
      </div>
    </div>
  );
}

// ─── Styles ───
const kpiPillStyle = (color, bg) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 10px', borderRadius: 'var(--radius-full)',
  background: bg, color: color, fontSize: 11, fontWeight: 500,
  border: `1px solid ${color}20`,
});

const statusBadge = (color, bg) => ({
  display: 'inline-flex', alignItems: 'center', gap: 3,
  padding: '1px 8px', borderRadius: 'var(--radius-full)',
  background: bg, color: color, fontSize: 10, fontWeight: 600,
});

const ghostBtnStyle = {
  padding: '8px 16px', borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-primary)', background: 'transparent',
  color: 'var(--text-secondary)', cursor: 'pointer',
  fontSize: 'var(--text-xs)', fontWeight: 500,
};

const notifyBtnStyle = {
  ...ghostBtnStyle,
  color: 'var(--info)', borderColor: 'var(--info)',
  display: 'flex', alignItems: 'center', gap: 6,
};

const labelStyle = {
  display: 'block', color: 'var(--text-secondary)', fontSize: 'var(--text-xs)',
  fontWeight: 600, marginBottom: 6,
};

const inputStyle = {
  width: '100%', padding: '10px 14px',
  borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)',
  background: 'var(--input-bg)', color: 'var(--text-primary)',
  fontSize: 'var(--text-sm)', outline: 'none', boxSizing: 'border-box',
};

const checkboxLabel = {
  display: 'flex', alignItems: 'center', gap: 8,
  color: 'var(--text-secondary)', fontSize: 'var(--text-sm)',
  cursor: 'pointer',
}